
import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export const HeroSection: React.FC = () => {
  const [currentImage, setCurrentImage] = useState<'before' | 'after'>('before');

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home" 
      className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/02a7afcefb1dfc1ac5d9e47e0123b243.jpg')`
      }}
    >
      <div className="container mx-auto px-6 py-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Your Airbnb Listing Photos Are Costing You{' '}
              <span className="text-yellow-400">$12,000+ Per Year.</span>{' '}
              We Can Fix That.
            </h1>
            
            <p className="text-xl lg:text-2xl mb-8 text-gray-200">
              Transform your existing photos into booking magnets with advanced AI enhancement – starting at just{' '}
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-yellow-400 font-bold hover:text-yellow-300 underline cursor-pointer"
              >
                $250 this week
              </a>.{' '}
              <button 
                onClick={scrollToServices}
                className="text-yellow-400 hover:text-yellow-300 underline cursor-pointer"
              >
                No new photoshoot needed
              </button>!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="#contact" 
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                  >
                    Get Professional Photos
                  </a>
                  <Link 
                    to="/whitepaper"
                    className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    Free Research Report
                  </Link>
                </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-4 text-yellow-400">Are you losing out? Listings with poor photos experience:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center">
                  <i className="ri-arrow-down-line text-red-400 mr-2"></i>
                  <span>
                    <Link to="/roi-calculator" className="hover:text-yellow-300 underline">
                      40% Fewer Bookings
                    </Link>
                  </span>
                </div>
                <div className="flex items-center">
                  <i className="ri-arrow-down-line text-red-400 mr-2"></i>
                  <span>
                    <Link to="/pricing-guide" className="hover:text-yellow-300 underline">
                      26% Lower Nightly Rates
                    </Link>
                  </span>
                </div>
                <div className="flex items-center">
                  <i className="ri-arrow-down-line text-red-400 mr-2"></i>
                  <span>2.5X Fewer Bookings vs Competitors</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-arrow-down-line text-red-400 mr-2"></i>
                  <span>
                    <Link to="/roi-calculator" className="hover:text-yellow-300 underline">
                      $4,000+ Lost Revenue/Year
                    </Link>
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-white rounded-lg p-6 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Before & After Transformation</h3>
              
              <div className="relative overflow-hidden rounded-lg mb-4">
                <img
                  src={currentImage === 'before' 
                    ? "https://readdy.ai/api/search-image?query=Poor%20quality%20Airbnb%20listing%20photo%2C%20dark%20lighting%2C%20blurry%20image%2C%20amateur%20photography%2C%20dull%20colors%2C%20unprofessional%20real%20estate%20photo%2C%20needs%20improvement%2C%20low%20resolution%2C%20bad%20exposure%2C%20unappealing%20rental%20property%20interior&width=500&height=300&seq=before-photo&orientation=landscape"
                    : "https://readdy.ai/api/search-image?query=Professional%20enhanced%20Airbnb%20listing%20photo%2C%20bright%20vibrant%20lighting%2C%20crystal%20clear%20sharp%20image%2C%20professional%20real%20estate%20photography%2C%20vivid%20colors%2C%20high-end%20property%20presentation%2C%20perfect%20exposure%2C%20stunning%20rental%20property%20interior%2C%20AI%20enhanced%20quality&width=500&height=300&seq=after-photo&orientation=landscape"
                  }
                  alt={currentImage === 'before' ? 'Before Enhancement' : 'After Enhancement'}
                  className="w-full h-64 object-cover"
                />
                
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    currentImage === 'before' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-green-500 text-white'
                  }`}>
                    {currentImage === 'before' ? 'BEFORE' : 'AFTER'}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setCurrentImage('before')}
                  className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors cursor-pointer ${
                    currentImage === 'before'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Before
                </button>
                <button
                  onClick={() => setCurrentImage('after')}
                  className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors cursor-pointer ${
                    currentImage === 'after'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  After
                </button>
              </div>
              
              <div className="text-center">
                <p className="text-gray-600 mb-2">Result: +67% more bookings</p>
                <p className="text-2xl font-bold text-green-600">+$1,025/month revenue</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
